//@flow

import type { ChildrenArray, Node } from "react";
import * as React from "react";
import { CSSTransition } from "react-transition-group";
import TransitionGroup from "react-transition-group/TransitionGroup";

import { SliderSteps } from "@/components";
import * as style from "@/components/Slider/slider.module.scss";
import { classNameOf } from "@/utils";

/**
 * A callback to let the slider know what slide finished loading.
 */
type DidLoadCallback = (index: number) => void;

/**
 * List of slides.
 */
type ChildrenType = ChildrenArray<Node>;

/**
 * Children function type, passes a callback function to children to let the slider know when a specific slide finishes loading.
 */
type ChildrenFunctionType = (didLoad: DidLoadCallback) => ChildrenType;

type Props = {
    initialSlide?: number,
    /**
     * Allow only children of type {@link SliderFrame}.
     */
    children: ChildrenFunctionType | ChildrenType,
    autoPlay?: boolean | number,
    styleClass?: string,

    /**
     * Callback when slide starts transitioning to the new slide.
     * @param slide
     */
    onChanged?: (slide: number) => void,
};

type SlideState = {
    /**
     * The index of the previous slide that was active,
     * this slide remains visible (on top of the current slide)
     * until the current slide finishes loading.
     */
    prev: number | null,

    /**
     * The index of the current slide.
     */
    current: number,

    timeoutID?: TimeoutID,
};

type AutoPlayOptions = {
    enabled: boolean,
    duration: number,
};

function getAutoPlayOptions(autoPlay?: boolean | number): AutoPlayOptions {
    if (typeof autoPlay === "number") {
        return { enabled: true, duration: autoPlay };
    }
    if (typeof autoPlay === "boolean" && autoPlay) {
        return { enabled: true, duration: 3000 };
    }
    const enabled = typeof autoPlay === "boolean" ? autoPlay : true;
    return { enabled, duration: 3000 };
}

export default class Slider extends React.Component<Props, SlideState> {
    +children: ChildrenArray<Node>;
    +childCount: number;
    +autoPlay: AutoPlayOptions;
  
    constructor(props: Props) {
        super(props);

        this.state = {
            prev: null,
            current: props.initialSlide || 0,
        };

        this.autoPlay = getAutoPlayOptions(props.autoPlay);

        const {children} = props;
        const didLoad: (index: number) => void = this.didLoad.bind(this);

        this.children = typeof children === "function"
            ? children(didLoad)
            : children;

        this.childCount = React.Children.count(this.children);
    }
  
    /**
     * Callback when a slide finished loading, removes the previous slide.
     *
     * @param index
     */
    didLoad: ((index: number) => void) = (index: number) => {
        this.setState(
            prevState => {
                if (prevState.current === index) {
                    return {
                        ...prevState,
                        prev: null,
                    };
                }

                return prevState;
            },
        );
    };
    
    /**
     * If auto-play is enabled, set a timeout that increments the current slide by one.
     * The timeoutID will be stored in state and will be cleared in setSlide.
     */
    scheduleNextSlide() {
        clearTimeout(this.state.timeoutID);
        
        const {enabled, duration} = this.autoPlay;

        if (enabled) {
            const {current} = this.state;

            const timeoutID = setTimeout(
                () => {
                    const next = current + 1;
                    if (next >= this.childCount) {
                        this.setSlide(0);
                    } else {
                        this.setSlide(next);
                    }
                },
                duration,
            );
            
            this.setState(
                prevState => ({
                    ...prevState,
                    timeoutID,
                }),
            );
        }
    }
    
    /**
     * Boundary check the slide number, ensures that it is not below zero or over the child count.
     */
    boundaryChecked(slide: number): number {
        return Math.max(0, Math.min(this.childCount - 1, slide));
    }
    
    onChanged(index: number) {
        this.scheduleNextSlide();

        if (typeof this.props.onChanged === "function") {
            this.props.onChanged(index);
        }
    }
    
    componentDidUpdate(prevProps: Props, prevState: SlideState) {
        const didChange = this.state.prev == null && prevState.prev !== null || 
            this.state.prev == null && this.state.current !== prevState.current;
        
        if (didChange) {
            this.onChanged(this.state.current);
        }
    }
    
    setSlide(index: number) {
        clearTimeout(this.state.timeoutID);

        const next = this.boundaryChecked(index);
        // If children prop doesn't have didLoad callback, set prev to null immediately
        if (typeof this.props.children !== "function") {
            this.setState(
                prevState => ({
                    ...prevState,
                    prev: null,
                    current: next,
                }),
            );
        } else {
            this.setState(
                prevState => ({
                    ...prevState,
                    prev: prevState.current === next ? prevState.prev : prevState.current,
                    current: next,
                }),
            );
        }
    }
    
    componentDidMount() {
        this.onChanged(this.state.current);
    }
    
    componentWillUnmount() {
        clearTimeout(this.state.timeoutID);
    }
    
    render(): React$Element<"div"> {
        const {styleClass} = this.props;
        const {current, prev} = this.state;
        const childrenArray = React.Children.toArray(this.children);
        /**
             * Selects the children to render.
             * If state.prev is not null, render both the current and previous slide.
             * If state.prev is null, only render the current slide
             *
             * Include the slide index as a key to prevent re-renders (Prevents flashing background image).
             *
             * @type {{ key: number, node: Node}[]}
             */
        const childrenToRender = typeof prev === "number"
            ? [
                    { key: current, node: childrenArray[current] },
                    { key: prev, node: childrenArray[prev] },
                ]
            : [{ key: current, node: childrenArray[current] }];
        
        return (
            <div className={classNameOf("slider", style.slider, styleClass || "")}>
                <TransitionGroup className={classNameOf(style.frameContainer, "slider-frame-container")}>
                    {childrenToRender.map(
                        item => <CSSTransition
                            key={item.key}
                            timeout={750}
                            classNames="fade-out">
                            {item.node}
                        </CSSTransition>,
                    )}
                </TransitionGroup>
                
                <div className={style.controls}>
                    <SliderSteps
                        current={current}
                        count={this.childCount}
                        onSlideSelected={slide => this.setSlide(slide)}
                    />
                </div>
            </div>
        );
    }
}

// const A = Slider;

// export default Slider;
